import {Map} from 'maplibre-gl';
import { MapContent, MapContentBase } from '../MapContent';
import {LocationInfo} from '../../../../models/AddressLookup';
import {BackendLocationModelWithTime} from '../../../../models/location.model';
import {PointFeature} from '../../../../models/GeoJson';
import {MapContentSource} from '../MapContentSource';
import {MapTools} from '../../../../tools/MapTools';
import {AddressMarkerLayer} from './AddressMarkerLayer';
import {AddressBreadcrumbLayer} from './AddressBreadcrumbLayer';
import {FeatureCollection} from 'geojson';
import {TrackStyles} from '../../../../../configuration/model/TrackStyles';
import {ShiftTrackLayer} from './ShiftTrackLayer';
import {ShiftTrackArrowsLayer} from './ShiftTrackArrowsLayer';
import { Protocol } from 'pmtiles';

export class AddressLookupPassMapContent extends MapContentBase implements MapContent {

  private source: MapContentSource = new MapContentSource('only-source');
  private tracksSource: MapContentSource = new MapContentSource('tracks-source');
  private markerLayer: AddressMarkerLayer = new AddressMarkerLayer(this.source);
  private breadcrumbLayer: AddressBreadcrumbLayer = new AddressBreadcrumbLayer(this.source);
  private shiftTrackLayer: ShiftTrackLayer;
  private shiftTrackArrowsLayer: ShiftTrackArrowsLayer = new ShiftTrackArrowsLayer(this.tracksSource);

  private readonly breadcrumbClickListener: any;

  constructor(
    marker: LocationInfo,
    breadcrumbs: BackendLocationModelWithTime[],
    shiftTrackFeatureCollection: FeatureCollection,
    trackStyles: TrackStyles,
    private closestBreadcrumbId: number,
    private breadcrumbHighlighted: (breadcrumbId: number) => void,
  ) {
    super();

    this.markerLayer.addMarker(marker);
    breadcrumbs.forEach(breadcrumb =>
      this.breadcrumbLayer.addBreadcrumb(breadcrumb, closestBreadcrumbId === breadcrumb.id));

    this.breadcrumbClickListener = (e) => {
      if (MapTools.checkPropagation(e)) {
        return;
      }
      const pointFeature = e.features[0] as PointFeature;
      this.breadcrumbClicked(pointFeature.properties.id);
    };

    this.tracksSource.loadFromFeatureCollection(shiftTrackFeatureCollection);
    this.shiftTrackLayer = new ShiftTrackLayer(this.tracksSource, trackStyles, null);
  }

  load(map: Map, protocol: Protocol, zoomToFeatures: boolean) {
    this.map = map;
    this.firstSymbolLayerId = this.getFirstSymbolLayerId();

    this.source.updateMap(this.map);
    this.tracksSource.updateMap(this.map);
    this.map.addLayer(this.shiftTrackLayer?.toLayerSpecification(), this.firstSymbolLayerId);
    this.map.addLayer(this.shiftTrackArrowsLayer.toLayerSpecification(), this.firstSymbolLayerId);
    this.map.addLayer(this.markerLayer.toLayerSpecification());
    this.map.addLayer(this.breadcrumbLayer.toLayerSpecification());

    MapTools.pointCursorForLayer(this.map, this.breadcrumbLayer, true);
    this.map.on('click', this.breadcrumbLayer.layerId, this.breadcrumbClickListener);

    if (zoomToFeatures) {
      this.zoom();
    }
  }

  unload() {
    MapTools.pointCursorForLayer(this.map, this.breadcrumbLayer, false);
    this.map.off('click', this.breadcrumbLayer.layerId, this.breadcrumbClickListener);

    this.map.removeLayer(this.markerLayer.layerId);
    this.map.removeLayer(this.breadcrumbLayer.layerId);
    this.map.removeLayer(this.shiftTrackLayer.layerId);
    this.map.removeLayer(this.shiftTrackArrowsLayer.layerId);
    this.map.removeSource(this.source.sourceId);
    this.map.removeSource(this.tracksSource.sourceId);
    this.map = null;
  }

  zoom() {
    MapTools.onMap(this.map, () => {
      this.markerLayer.zoomToVisibleFeatures(this.map, 16);
    });
  }

  breadcrumbClicked(breadcrumbId: number) {
    this.highlightBreadcrumb(breadcrumbId);
  }

  private highlightBreadcrumb(breadcrumbId: number) {
    this.source.features.forEach(feature => {
      // skip found addresses and closest breadcrumb
      if (feature.properties.type === 'breadcrumb' && feature.properties.id !== this.closestBreadcrumbId) {
        feature.properties.status = (breadcrumbId === feature.properties.id) ? 'highlighted' : 'normal';
      }
    });

    this.source.updateMap(this.map);
    this.breadcrumbHighlighted(breadcrumbId);
  }
}
